import { api, apiBlob } from '../../../lib/api';
import { reauthHeaders } from '../../../lib/reauth';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  EvidenceReceivingGroup,
  UpdateEvidenceContainerRequest,
  EvidenceTransferResult,
  TransferEvidenceRequest,
  CreateEvidenceReceiptRequest,
  EvidenceReceiptLine,
  EvidenceReceiptListItem,
  EvidenceAccessLogItem,
  EvidenceBatchResult,
  EvidenceCaseListItem,
  EvidenceCaseListQuery,
  EvaluateEvidenceRequest,
  EvidenceContainerGroup,
  EvidenceChainVerification,
  EvidenceEligibility,
  EvidenceFileTypeItem,
  EvidenceItemDetail,
  EvidenceItemListItem,
  EvidenceItemListQuery,
  EvidenceItemMarkingsRequest,
  EvidenceItemRequest,
  EvidenceKindListItem,
  EvidenceKindOption,
  EvidenceKindRequest,
  EvidenceRowFiles,
  EvidenceStorageLocationListItem,
  EvidenceStorageLocationOption,
  EvidenceStorageLocationRequest,
  EvidenceTermListItem,
  EvidenceTermOption,
  EvidenceTermRequest,
  RequestEvidenceMovementRequest,
  StoreEvidenceBatchRequest,
  StoreInEvidenceItemRequest,
  UpdateEvidenceFileTypeRequest,
  EvidenceRequestDetail,
  EvidenceRequestKind,
} from './evidenceTypes';
import { REQUEST_KIND } from './evidenceTypes';

/*
  Every facet is written whenever its KEY IS PRESENT, empty string included — an empty value is
  the reader saying "none of these". A truthiness check would drop it and the server would read
  the axis as unmentioned, which is the opposite result.
*/
function baseParams(query: ListQuery): URLSearchParams {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.status !== undefined) params.set('status', query.status);

  return params;
}

function qs(params: URLSearchParams): string {
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

const CASES = '/api/evidence-cases';
const ITEMS = '/api/evidence-items';
const LOCATIONS = '/api/evidence-storage-locations';
const RECEIPTS = '/api/evidence-receipts';
const KINDS = '/api/evidence-kinds';
const TYPES = '/api/evidence-types';

/** The Evidence page's READ of the case registry — the cases the caller is in, and who read their evidence. */
export const evidenceCasesApi = {
  list: (query: EvidenceCaseListQuery, signal?: AbortSignal) => {
    const params = baseParams(query);
    if (query.caseStatus !== undefined) params.set('caseStatus', query.caseStatus);
    return api<PagedResult<EvidenceCaseListItem>>(`${CASES}${qs(params)}`, { signal });
  },

  /** Whether the caller is an employee — the Evidence page states it before the reader meets a refusal. */
  eligibility: (signal?: AbortSignal) => api<EvidenceEligibility>(`${CASES}/eligibility`, { signal }),

  /** Opens a case's evidence — logged server-side. */
  get: (id: string, signal?: AbortSignal) => api<EvidenceCaseListItem>(`${CASES}/${id}`, { signal }),

  // Registering, editing, classifying, deleting and sharing a case are Case Management's `casesApi`.

  accessLog: (id: string, query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<EvidenceAccessLogItem>>(`${CASES}/${id}/access-log${qs(baseParams(query))}`, { signal }),
};

export const evidenceItemsApi = {
  list: (query: EvidenceItemListQuery, signal?: AbortSignal) => {
    const params = baseParams(query);
    if (query.kind !== undefined) params.set('kind', query.kind);
    if (query.type !== undefined) params.set('type', query.type);
    if (query.container !== undefined) params.set('container', query.container);
    if (query.custody !== undefined) params.set('custody', query.custody);
    if (query.location !== undefined) params.set('location', query.location);
    if (query.caseId) params.set('caseId', query.caseId);
    if (query.groupByContainer) params.set('groupByContainer', 'true');
    if (query.groupByReceiving) params.set('groupByReceiving', 'true');
    return api<PagedResult<EvidenceItemListItem>>(`${ITEMS}${qs(params)}`, { signal });
  },

  /** The places this case's evidence was deferred to — the Transfer to Place dropdown. */
  places: (caseId: string, signal?: AbortSignal) =>
    api<string[]>(`${ITEMS}/places?caseId=${encodeURIComponent(caseId)}`, { signal }),

  /** A case's receiving events among its waiting items — the Received folder's headers. */
  receivings: (caseId: string, signal?: AbortSignal) =>
    api<EvidenceReceivingGroup[]>(`${ITEMS}/receivings?caseId=${encodeURIComponent(caseId)}`, { signal }),

  /** A container's custodian and agent. Its custodian only. */
  updateContainer: (request: UpdateEvidenceContainerRequest) =>
    api<void>(`${ITEMS}/containers`, { method: 'PUT', body: request }),

  /** Soft-deletes a container's items. Its custodian only; password-confirmed. */
  deleteContainer: (caseId: string, containerNumber: string | null, reauthentication: string) =>
    api<void>(`${ITEMS}/containers/delete`, {
      method: 'POST',
      body: { evidenceCaseId: caseId, containerNumber },
      headers: reauthHeaders(reauthentication),
    }),

  /** A case's containers — counts, custodian, agent, whether the caller holds it. The Stored in headers. */
  containers: (caseId: string, search?: string, signal?: AbortSignal) =>
    api<EvidenceContainerGroup[]>(
      `${ITEMS}/containers?caseId=${encodeURIComponent(caseId)}${search?.trim() ? `&search=${encodeURIComponent(search.trim())}` : ''}`,
      { signal },
    ),

  /** Opening an item is LOGGED server-side — a read of evidence is part of its record. */
  get: (id: string, signal?: AbortSignal) => api<EvidenceItemDetail>(`${ITEMS}/${id}`, { signal }),

  /**
   * Receives new evidence — one receiving event, every row in one request, all or nothing. Each
   * row's files travel under its own index, so a file can only belong to the row it names.
   */
  store: (request: StoreEvidenceBatchRequest, files: EvidenceRowFiles[]) =>
    api<EvidenceBatchResult>(ITEMS, { method: 'POST', body: batchForm(request, files) }),

  /** Accepts or defers received items — one decision for all of them. */
  evaluate: (request: EvaluateEvidenceRequest) =>
    api<void>(`${ITEMS}/evaluate`, { method: 'POST', body: request }),

  /** Receives back an item that was transferred out. */
  storeIn: (id: string, request: StoreInEvidenceItemRequest, attachments: File[]) =>
    api<EvidenceItemDetail>(`${ITEMS}/${id}/store-in`, {
      method: 'POST',
      body: form(request, null, attachments),
    }),

  update: (id: string, request: EvidenceItemRequest) =>
    api<EvidenceItemDetail>(`${ITEMS}/${id}`, { method: 'PUT', body: request }),

  /** The purpose. Moves nothing. */
  mark: (id: string, request: EvidenceItemMarkingsRequest) =>
    api<EvidenceItemDetail>(`${ITEMS}/${id}/markings`, { method: 'PUT', body: request }),

  /**
   * The Stored folder's TRANSFER — to a court or disposal (one approval for the batch), or a
   * deferral (immediate). Password-confirmed; the court order's file travels as `courtOrder`.
   */
  transfer: (request: TransferEvidenceRequest, courtOrder: File | null, reauthentication: string) => {
    const { itemIds, ...fields } = request;
    const body = form(fields, null, []);
    for (const id of itemIds) body.append('itemIds', id);
    if (courtOrder) body.append('courtOrder', courtOrder);

    return api<EvidenceTransferResult>(`${ITEMS}/transfer`, {
      method: 'POST',
      body,
      headers: reauthHeaders(reauthentication),
    });
  },

  /** Password-confirmed: spends the proof `verifyPassword` issued. */
  requestMovement: (id: string, request: RequestEvidenceMovementRequest, reauthentication: string) =>
    api<EvidenceItemDetail>(`${ITEMS}/${id}/movements`, {
      method: 'POST',
      body: request,
      headers: reauthHeaders(reauthentication),
    }),

  /** Password-confirmed. */
  deactivate: (id: string, reauthentication: string) =>
    api<void>(`${ITEMS}/${id}`, { method: 'DELETE', headers: reauthHeaders(reauthentication) }),

  activate: (id: string) => api<void>(`${ITEMS}/${id}/restore`, { method: 'POST' }),

  /** The cases endpoint's twin, under this module's own read key. */
  eligibility: (signal?: AbortSignal) => api<EvidenceEligibility>(`${ITEMS}/eligibility`, { signal }),

  verifyChain: (id: string, signal?: AbortSignal) =>
    api<EvidenceChainVerification>(`${ITEMS}/${id}/chain`, { signal }),

  /** Re-hashed server-side and refused on a mismatch. Every download is logged. */
  downloadFile: (id: string) => apiBlob(`${ITEMS}/${id}/file`),

  downloadAttachment: (id: string, attachmentId: string) =>
    apiBlob(`${ITEMS}/${id}/attachments/${attachmentId}/file`),
};

/**
 * A case's EVIDENCE REQUESTS — the Requests folder. Pending only. FULFILLING one has no call here: it is
 * `evidenceItemsApi.store` / `transfer` naming the request. Declining is one route per kind, each on the
 * key fulfilling that kind needs.
 */
export const evidenceCaseRequestsApi = {
  list: (caseId: string, query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<EvidenceRequestDetail>>(`${CASES}/${caseId}/requests${qs(baseParams(query))}`, { signal }),

  decline: (id: string, kind: EvidenceRequestKind, reason: string) =>
    api<void>(`/api/evidence-requests/${id}/${kind === REQUEST_KIND.receiving ? 'decline-receiving' : 'decline-transfer'}`, {
      method: 'POST',
      body: { reason },
    }),
};

/** A case's acknowledgement receipts. Read and create only — a receipt is permanent. */
export const evidenceReceiptsApi = {
  list: (caseId: string, query: ListQuery, signal?: AbortSignal) => {
    const params = baseParams(query);
    params.set('caseId', caseId);
    return api<PagedResult<EvidenceReceiptListItem>>(`${RECEIPTS}${qs(params)}`, { signal });
  },

  /** The case's items a new receipt may list — every live one. */
  candidates: (caseId: string, signal?: AbortSignal) =>
    api<EvidenceReceiptLine[]>(`${RECEIPTS}/candidates${qs(new URLSearchParams({ caseId }))}`, { signal }),

  create: (request: CreateEvidenceReceiptRequest) =>
    api<EvidenceReceiptListItem>(RECEIPTS, { method: 'POST', body: request }),
};

export const evidenceStorageLocationsApi = {
  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<EvidenceStorageLocationListItem>>(`${LOCATIONS}${qs(baseParams(query))}`, { signal }),

  options: (signal?: AbortSignal) =>
    api<EvidenceStorageLocationOption[]>(`${LOCATIONS}/options`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<EvidenceStorageLocationListItem>(`${LOCATIONS}/${id}`, { signal }),

  create: (request: EvidenceStorageLocationRequest) =>
    api<EvidenceStorageLocationListItem>(LOCATIONS, { method: 'POST', body: request }),

  update: (id: string, request: EvidenceStorageLocationRequest) =>
    api<EvidenceStorageLocationListItem>(`${LOCATIONS}/${id}`, { method: 'PUT', body: request }),

  /** Password-confirmed. */
  deactivate: (id: string, reauthentication: string) =>
    api<void>(`${LOCATIONS}/${id}`, { method: 'DELETE', headers: reauthHeaders(reauthentication) }),

  activate: (id: string) => api<void>(`${LOCATIONS}/${id}/restore`, { method: 'POST' }),
};

export const evidenceKindsApi = {
  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<EvidenceKindListItem>>(`${KINDS}${qs(baseParams(query))}`, { signal }),

  options: (signal?: AbortSignal) => api<EvidenceKindOption[]>(`${KINDS}/options`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<EvidenceKindListItem>(`${KINDS}/${id}`, { signal }),

  create: (request: EvidenceKindRequest) => api<EvidenceKindListItem>(KINDS, { method: 'POST', body: request }),

  update: (id: string, request: EvidenceKindRequest) =>
    api<EvidenceKindListItem>(`${KINDS}/${id}`, { method: 'PUT', body: request }),

  deactivate: (id: string) => api<void>(`${KINDS}/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`${KINDS}/${id}/restore`, { method: 'POST' }),
};

/** The three term vocabularies share one endpoint shape; each sits behind its own key. */
function termsApi(base: string) {
  return {
    list: (query: ListQuery, signal?: AbortSignal) =>
      api<PagedResult<EvidenceTermListItem>>(`${base}${qs(baseParams(query))}`, { signal }),

    options: (signal?: AbortSignal) => api<EvidenceTermOption[]>(`${base}/options`, { signal }),

    get: (id: string, signal?: AbortSignal) => api<EvidenceTermListItem>(`${base}/${id}`, { signal }),

    /** Idempotent on a live name — a second add of a word returns the one there. */
    create: (request: EvidenceTermRequest) => api<EvidenceTermListItem>(base, { method: 'POST', body: request }),

    update: (id: string, request: EvidenceTermRequest) =>
      api<EvidenceTermListItem>(`${base}/${id}`, { method: 'PUT', body: request }),

    deactivate: (id: string) => api<void>(`${base}/${id}`, { method: 'DELETE' }),

    activate: (id: string) => api<void>(`${base}/${id}/restore`, { method: 'POST' }),
  };
}


export const evidenceDisposalTypesApi = termsApi('/api/evidence-disposal-types');

export const evidenceTypesApi = termsApi(TYPES);

export const evidenceFileTypesApi = {
  list: (signal?: AbortSignal) => api<EvidenceFileTypeItem[]>('/api/evidence-file-types', { signal }),

  update: (id: string, request: UpdateEvidenceFileTypeRequest) =>
    api<EvidenceFileTypeItem>(`/api/evidence-file-types/${id}`, { method: 'PUT', body: request }),
};

/**
 * A multipart body: the request's fields by name, the evidence `file`, and each `attachments`.
 * A null field is LEFT OUT rather than sent as "null", which the binder would read as a string.
 */
function form(fields: object, file: File | null, attachments: File[]): FormData {
  const body = new FormData();

  for (const [key, value] of Object.entries(fields)) {
    if (value !== null && value !== undefined) body.append(key, String(value));
  }

  if (file) body.append('file', file);
  for (const attachment of attachments) body.append('attachments', attachment);

  return body;
}

/**
 * The receiving event as multipart: the header's fields by name, each row's as `rows[i].field`,
 * and each row's files as `rows[i].file` and `rows[i].attachments` — the names the controller
 * picks them out by.
 */
function batchForm(request: StoreEvidenceBatchRequest, files: EvidenceRowFiles[]): FormData {
  const { rows, ...header } = request;
  const body = new FormData();

  for (const [key, value] of Object.entries(header)) {
    if (value !== null && value !== undefined) body.append(key, String(value));
  }

  rows.forEach((row, index) => {
    for (const [key, value] of Object.entries(row)) {
      // Left out when absent, as the header's are — an optional `requestLineId` must not arrive as "undefined".
      if (value !== null && value !== undefined) body.append(`rows[${index}].${key}`, String(value));
    }

    const rowFiles = files[index];
    if (rowFiles?.file) body.append(`rows[${index}].file`, rowFiles.file);
    for (const attachment of rowFiles?.attachments ?? []) body.append(`rows[${index}].attachments`, attachment);
  });

  return body;
}
